// FORM: https://github.com/modusign/modusign-client/blob/1f2cc00e317b3cc6ddf42ace319c72036470a423/app/src/components/SignatureMarkerField/index.tsx
import { Box, Text, Toast } from '@modusign/design-system';
import { useSignatureField } from '@modusign/react-signing';
import type { FieldNode, SignatureField, Size } from '@modusign/react-signing/types';
import { cx } from 'emotion';
import isEqual from 'lodash/isEqual';
import { useCallback, useEffect, useState } from 'react';
import OutsideClickHandler from 'react-outside-click-handler';
import { useToggle } from 'react-use';

import RoundedXButton from '@app/components/Buttons/RoundedXButton';
import { FieldBase } from '@app/features/apply-react-signing/components/FieldRenderer/components/FieldBase';
import {
  toAbsolutePoint,
  toAbsoluteSize,
  toRelativePoint,
  toRelativeSize,
} from '@app/features/apply-react-signing/components/FieldRenderer/libs/absRelConverter';
import { clampFieldPoint } from '@app/features/apply-react-signing/components/FieldRenderer/libs/clampFieldPoint';
import { createAbsolutePoint } from '@app/features/apply-react-signing/components/FieldRenderer/libs/createAbsolutePoint';
import { createAbsoluteSize } from '@app/features/apply-react-signing/components/FieldRenderer/libs/createAbsoluteSize';
import { getDefaultSignatureSize } from '@app/features/apply-react-signing/components/FieldRenderer/libs/getDefaultSignatureSize';
import { getSignerDisplayName } from '@app/features/apply-react-signing/components/FieldRenderer/libs/getSignerDisplayName';
import {
  AbsolutePoint,
  AbsoluteSize,
  Point,
  ReactSigningFieldRendererProps,
} from '@app/features/apply-react-signing/components/FieldRenderer/types';
import { SignatureFieldPlaceholder } from '@app/features/apply-react-signing/components/FieldSetupStep/Placeholders/SignatureFieldPlaceholder';
import { SignatureFieldContextMenu } from '@app/features/apply-react-signing/components/FieldSetupStep/SignatureFieldContextMenu';
import { useOptimalFieldsContext } from '@app/features/apply-react-signing/libs/hooks/useCollectionOptimal';
import { isSignerResizableSignatureField } from '@app/features/apply-react-signing/libs/isSignerResizableSignatureField';
import {
  calculateSignatureResizeAdjustedSize,
  getMaxSignatureFieldSize,
  getMinSignatureFieldSize,
} from '@app/features/apply-react-signing/libs/signatureFieldSize';
import createFieldClassNameByParticipantOrder from '@app/libs/participantFields/createFieldClassNameByParticipantOrder';

import './ConfigurableSignatureField.scss';

function calculateOffset(position: Point, size: Size) {
  return {
    x: Math.max(0, -position.x) + Math.min(0, 1.0 - (position.x + size.width)),
    y: Math.max(0, -position.y) + Math.min(0, 1.0 - (position.y + size.height)),
  };
}

function anchorAdjustedBox(position: Point, size: Size, anchor: Point) {
  return {
    position: {
      x: position.x - size.width * anchor.x,
      y: position.y - size.height * anchor.y,
    },
    size: {
      width: size.width,
      height: size.height,
    },
  };
}

function getPositionMovedIntoPage(field: SignatureField, viewport: AbsoluteSize, scale: number) {
  const relativeDefaultSize = toRelativeSize(
    getDefaultSignatureSize(field?.config.allow ?? [], scale),
    viewport
  );

  const signerResizable = isSignerResizableSignatureField(field);
  const size = signerResizable ? relativeDefaultSize : field.size;
  const anchorPoint = signerResizable ? { x: 0.5, y: 0.5 } : { x: 0, y: 0 };

  const leftTopAnchorBox = anchorAdjustedBox(field.position, size, anchorPoint);
  const offset = calculateOffset(leftTopAnchorBox.position, leftTopAnchorBox.size);

  return {
    x: field.position.x + offset.x,
    y: field.position.y + offset.y,
  };
}

export function ConfigurableSignatureFieldRenderer({
  fieldId,
  readOnly,
  restrictModification,
  viewport,
  scale,
}: ReactSigningFieldRendererProps) {
  const [isResizing, setResizing] = useState(false);
  const { field, setPosition, setSize } = useSignatureField(fieldId);

  const { removeField, changeFields } = useOptimalFieldsContext<'SIGNATURE', 'SIGNER'>();
  // TODO(modusign/react-signing#66): useFields는 전체 컬랙션을 참조하기 때문에 성능이슈가 있을 수 있습니다. 해당 이슈가 해결된 뒤 hook에서 전달되는 index를 이용한니다.
  const [isDragging, setIsDragging] = useState(false);
  const signerResizable = isSignerResizableSignatureField(field);
  const [isContextMenuOpen, toggleIsContextMenuOpen] = useToggle(true);
  const isStampField = isEqual(field?.config.allow, ['STAMP']);

  const handlePointChange = useCallback(
    (point: AbsolutePoint) => {
      if (!field) {
        return;
      }

      const defaultSize = getDefaultSignatureSize(field?.config.allow, scale);
      const offset = signerResizable
        ? {
            x: defaultSize.width / 2,
            y: defaultSize.height / 2,
          }
        : { x: 0, y: 0 };

      const absolutePoint = createAbsolutePoint({
        x: point.x + offset.x,
        y: point.y + offset.y,
      });

      const relativePosition = clampFieldPoint(toRelativePoint(absolutePoint, viewport));
      setPosition(relativePosition.x, relativePosition.y);
    },
    [field, scale, setPosition, signerResizable, viewport]
  );

  const moveInPageIfSizedSignatureFieldOut = useCallback(() => {
    /**
     * 과거 width:0, height:0 이었던 필드는 (width/2, height/2) 만큼 문서 밖으로 나갈 수 있도록 정책이 되어있었습니다.
     * 이 정책을 이용해 간인 등의 잘못된 사용케이스들을 발생하는 문제가 있었습니다.
     * 이런 케이스들을 막기 위해 문서 밖으로 나가있는 서명 필드가 있을 경우 문서 안으로 이동시킵니다.
     */
    if (field) {
      const positionMovedIntoPage = getPositionMovedIntoPage(field, viewport, scale);
      setPosition(positionMovedIntoPage.x, positionMovedIntoPage.y);
    }
  }, [field, scale, setPosition, viewport]);

  useEffect(() => {
    if (!readOnly) {
      moveInPageIfSizedSignatureFieldOut();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleToggleDisableSignatureGenerator = useCallback(() => {
    changeFields((fields) => {
      return fields.map<FieldNode>((f) => {
        const iterField = f as FieldNode;

        if (
          iterField.type === 'SIGNATURE' &&
          iterField.participantReference.id === field?.participantReference.id
        ) {
          return {
            ...iterField,
            config: {
              ...iterField.config,
              disableSignatureGenerator: !field.config.disableSignatureGenerator,
            },
          };
        }

        return iterField;
      });
    });
    Toast.success({
      description: (
        <Text fontSize="md" fontWeight="regular">
          해당 서명자의 모든 도장 설정이 변경되었습니다.
        </Text>
      ),
    });
  }, [changeFields, field?.config.disableSignatureGenerator, field?.participantReference.id]);

  const handleResize = useCallback(
    (size: AbsoluteSize) => {
      const relativeSize = toRelativeSize(
        calculateSignatureResizeAdjustedSize(field?.config.allow ?? [], size),
        viewport
      );
      setSize(relativeSize.width, relativeSize.height);
    },
    [field?.config.allow, setSize, viewport]
  );

  const handleToggleResizeOptionToggle = useCallback(() => {
    const nextSignerResizable = !signerResizable;

    const defaultSize = getDefaultSignatureSize(field?.config.allow ?? [], scale);
    changeFields((fields) => {
      return fields.map<FieldNode>((f) => {
        if (
          f.type === 'SIGNATURE' &&
          f.participantReference.id === field?.participantReference.id
        ) {
          const point = toAbsolutePoint(f.position, viewport);
          const offset = nextSignerResizable
            ? {
                x: defaultSize.width / 2,
                y: defaultSize.height / 2,
              }
            : {
                x: -defaultSize.width / 2,
                y: -defaultSize.height / 2,
              };

          const absolutePoint = createAbsolutePoint({
            x: point.x + offset.x,
            y: point.y + offset.y,
          });

          return {
            ...f,
            position: {
              ...f.position,
              ...toRelativePoint(absolutePoint, viewport),
            },
            size: toRelativeSize(
              createAbsoluteSize(nextSignerResizable ? { width: 0, height: 0 } : defaultSize),
              viewport
            ),
          };
        }

        return f;
      });
    });

    changeFields((fields) => {
      return fields.map((field) => {
        if (
          field.type === 'SIGNATURE' &&
          field.participantReference.id === field?.participantReference.id
        ) {
          return {
            ...field,
            position: {
              ...field.position,
              ...getPositionMovedIntoPage(field, viewport, scale),
            },
          };
        }
        return field;
      });
    });

    Toast.success({
      description: (
        <Text fontSize="md" fontWeight="medium">
          {nextSignerResizable
            ? '해당 서명자의 모든 사인·도장이 서명자가 크기를 조절할 수 있게 변경되었습니다.'
            : '해당 서명자의 모든 사인·도장이 서명자가 크기를 조절할 수 없게 변경되었습니다.'}
        </Text>
      ),
    });
  }, [
    changeFields,
    field?.config.allow,
    field?.participantReference.id,
    scale,
    signerResizable,
    viewport,
  ]);

  if (!field || field.participantReference.type !== 'SIGNER') {
    return null;
  }

  const absoluteSize = signerResizable
    ? getDefaultSignatureSize(field.config.allow, scale)
    : toAbsoluteSize(field.size, viewport);

  const minSize = getMinSignatureFieldSize(field.config.allow, scale);
  const maxSize = getMaxSignatureFieldSize(field.config.allow, scale);

  // TODO (@Dave): 정확한 역할 확인하고 네이밍 고민해보기
  const displayName = getSignerDisplayName(field.participantReference);

  const participantOrder = field.participantReference.index + 1;

  const ConfigurableSignatureFieldClassByOrder = createFieldClassNameByParticipantOrder(
    'ConfigurableSignatureField',
    participantOrder
  );
  const shouldLockRatio = !isEqual(field?.config.allow, ['SIGN', 'STAMP']);

  const point = toAbsolutePoint(field.position, viewport);

  const offset = signerResizable
    ? {
        x: -absoluteSize.width / 2,
        y: -absoluteSize.height / 2,
      }
    : {
        x: 0,
        y: 0,
      };

  const absolutePoint = createAbsolutePoint({
    x: point.x + offset.x,
    y: point.y + offset.y,
  });

  return (
    <FieldBase
      className={cx({
        ConfigurableSignatureField: true,
        [ConfigurableSignatureFieldClassByOrder]: true,
      })}
      disableDrag={isResizing}
      fieldGuideline={{
        guideLineOffset: {
          left: 0,
          right: 0,
          top: 0,
          bottom: 0,
        },
        viewport,
        showGuideLine: isDragging,
      }}
      lockAspectRatio={shouldLockRatio}
      maxConstraints={[
        Math.min(maxSize.width, viewport.width - absolutePoint.x),
        Math.min(maxSize.height, viewport.height - absolutePoint.y),
      ]}
      minConstraints={[minSize.width, minSize.height]}
      point={absolutePoint}
      readOnly={readOnly}
      resizeHandles={signerResizable || readOnly || restrictModification ? [] : ['se']}
      restrictModification={restrictModification}
      size={absoluteSize}
      onPointChange={handlePointChange}
      onPointChangeStart={() => {
        setIsDragging(true);
        toggleIsContextMenuOpen(true);
      }}
      onPointChangeStop={() => {
        setIsDragging(false);
        moveInPageIfSizedSignatureFieldOut();
      }}
      onSizeChange={handleResize}
      onSizeChangeStart={() => setResizing(true)}
      onSizeChangeStop={() => {
        setResizing(false);
        moveInPageIfSizedSignatureFieldOut();
      }}
    >
      <OutsideClickHandler onOutsideClick={() => toggleIsContextMenuOpen(false)}>
        {isContextMenuOpen && !readOnly && !restrictModification && (
          <Box
            bottom={`${absoluteSize.height + 24}px`}
            left={`${absoluteSize.width / 2}px`}
            position="absolute"
            style={{
              transform: 'translateX(-50%)',
            }}
          >
            <SignatureFieldContextMenu
              disabledSignatureGenerator={
                isStampField ? field.config.disableSignatureGenerator : undefined
              }
              displayName={displayName}
              participantOrder={participantOrder}
              signerResizable={signerResizable}
              onToggleDisableSignatureGeneratorClicked={
                isStampField ? handleToggleDisableSignatureGenerator : undefined
              }
              onToggleSignerResizeOption={handleToggleResizeOptionToggle}
            />
          </Box>
        )}
        <SignatureFieldPlaceholder
          allow={field.config.allow}
          participantOrder={participantOrder}
          size={absoluteSize}
          uploadOnly={field.config.disableSignatureGenerator}
        />
        {!readOnly && !restrictModification && (
          <Box className="ConfigurableSignatureField__x-button">
            <RoundedXButton onClick={() => removeField(field.id)} />
          </Box>
        )}
      </OutsideClickHandler>
    </FieldBase>
  );
}
